import { inject, Service } from '@angular/core'
import { BaseParticipant, ParticipantService } from '@superlikers/microsites-api'
import { firstValueFrom, map } from 'rxjs'

import { CATEGORIES, Category } from '@core/data/models'
import { Venue, VenueManager } from '../../models/venue.model'

const VENUE_TAG = 'centro_de_consumo'
const MANAGER_TAG = 'gerente'
const VENUE_TAG_PREFIX = 'cdc'

/** Todos los participantes llevan el tag `admin`, así que hay que pedirlos. */
const WITH_ADMINS = true
const PAGE_SIZE = 100

const CATEGORY_VALUES = Object.values(CATEGORIES)

/** Campos de esta campaña en el participante del listado. */
interface VenueParticipant extends BaseParticipant {
  category: string
}

/**
 * HTTP puro de los centros de consumo y única pieza que sabe de dónde salen:
 * son participantes con el tag `centro_de_consumo`, y el tag `cdc_N` es lo que
 * los relaciona con su gerente y con el ejecutivo que los atiende.
 *
 * Devuelve interfaces del proyecto. Sin lógica de negocio; lanza, no maneja errores.
 */
@Service()
export class VenuesApi {
  readonly #participants = inject(ParticipantService)

  /** Centros que corresponden a los tags dados, en el orden de esos tags. */
  async getVenues(venueTags: string[]): Promise<Venue[]> {
    if (venueTags.length === 0) return []

    const participants = await this.#getByTag(VENUE_TAG)

    return venueTags.flatMap(tag => {
      const participant = participants.find(item => this.#venueTagOf(item) === tag)

      return participant ? [this.#venueAdapter(participant, tag)] : []
    })
  }

  /** Gerente de cada centro, por tag. Un centro puede no tener gerente todavía. */
  async getManagers(venueTags: string[]): Promise<Map<string, VenueManager>> {
    if (venueTags.length === 0) return new Map()

    const participants = await this.#getByTag(MANAGER_TAG)
    const managers = new Map<string, VenueManager>()

    for (const participant of participants) {
      const tag = this.#venueTagOf(participant)

      // El listado no devuelve el uid del propio usuario que consulta.
      if (!tag || !venueTags.includes(tag) || !participant.uid || managers.has(tag)) continue

      managers.set(tag, { id: participant.uid, name: participant.name })
    }

    return managers
  }

  async #getByTag(tag: string): Promise<VenueParticipant[]> {
    const list$ = this.#participants.list<VenueParticipant>({ page: 1, limit: PAGE_SIZE, tags: [tag], with_admins: WITH_ADMINS }).pipe(map(response => response.data))

    return firstValueFrom(list$)
  }

  #venueAdapter(participant: VenueParticipant, tag: string): Venue {
    return {
      id: participant.uid,
      tag,
      name: participant.name,
      category: this.#toCategory(participant.category)
    }
  }

  #venueTagOf(participant: VenueParticipant): string | undefined {
    return (participant.tags ?? []).find(tag => tag.startsWith(VENUE_TAG_PREFIX))
  }

  #toCategory(category: string): Category | null {
    return CATEGORY_VALUES.find(value => value === category) ?? null
  }
}
